/* @jsx h */
import {
  defineComponent,
  h,
  onMounted,
  onUnmounted,
  ref,
  provide,
  inject,
} from "vue";
import { useRouter } from "vue-router";
import { useToast } from "./Toast";
import { useNavStack, setStatusBarDispatcher } from "@lemmonhq/uikit";
import { setNativePrintDispatcher } from "../../data/api/nativePrint";
import { createBadgeSetter } from "./nativeBadge";
import { createRPC } from "rpc-anywhere";
import { createWebViewTransport } from "./bridge-transport-web";
import type {
  ScanResult,
  ScannerConfig,
  WebRPCSchema,
  NativeRPCSchema,
  RoutingConfig,
  UserIdentity,
} from "./bridge-rpc-types";

type NativeBridgeContextType = {
  rpc: ReturnType<typeof createRPC<WebRPCSchema, NativeRPCSchema>>;
  onScan: (callback: (result: ScanResult) => void) => void;
  openScanner: (config: ScannerConfig) => void;
  closeScanner: () => void;
  logout: () => void;
  identify: (identity: UserIdentity) => Promise<void>;
  setBadge: (pendingRequestIds: string[]) => Promise<void>;
  clearNotificationCenter: () => Promise<void>;
  switchServer: (id: "primary" | "secondary") => Promise<void>;
  getRoutingConfig: () => Promise<RoutingConfig>;
};

declare global {
  var lemmon: {
    push: {
      handle: (type: string, event: unknown) => void;
    };
    navigation: {
      handleBack: () => boolean;
    };
    scanner: {
      onScan: (result: ScanResult) => void;
    };
  };
  var ReactNativeWebView: {
    postMessage: Function;
  };
}

const NativeBridgeContextKey = Symbol("NativeBridgeContext");

// Routes where back button should exit the app instead of navigating back
const EXIT_ROUTES = ["/", "app/pos", "app/reports"];

const shouldExitOnBack = (path: string): boolean => {
  return EXIT_ROUTES.includes(path);
};

/**
 * NativeBridgeProvider sets up handlers for React Native WebView integration
 * - Push notification handling (pushReceived, pushOpened)
 * - Navigation back button handling
 * - Barcode/QR scanner integration
 */
export const NativeBridgeProvider = defineComponent({
  name: "NativeBridgeProvider",
  setup(_, { slots }) {
    const router = useRouter();
    const toast = useToast();
    const navStack = useNavStack();
    const scanCallback = ref<((result: ScanResult) => void) | null>(null);

    // Create RPC instance
    const rpcTransport = createWebViewTransport();
    const rpc = createRPC<WebRPCSchema, NativeRPCSchema>({
      transport: rpcTransport,
      maxRequestTime: 1000 * 1000,
      requestHandler: {
        // Handle back navigation requests from native
        handleBack() {
          // Close the topmost open sheet/modal first, if any
          if (navStack?.popTop()) {
            return true;
          }

          const currentPath = router.currentRoute.value.path;

          // At exit route - let native handle it (exit app)
          if (shouldExitOnBack(currentPath)) {
            console.log("At exit route, letting native exit app");
            return false;
          }

          // Navigate back in app
          console.log("Navigating back from:", currentPath);
          router.back();
          return true;
        },
      },
    });

    // Let the theme system drive the native status bar. useTheme flushes the
    // current style immediately on registration (covers initial load) and on
    // every theme change thereafter.
    // Native ePOS printing for printersAPI.printJob (used only when the app
    // advertises printEpos and the restaurant has native_print on).
    setNativePrintDispatcher((params) => rpc.request("printEpos", params));

    setStatusBarDispatcher((style) => {
      if (!(globalThis as any).ReactNativeWebView) return;
      rpc
        .request("changeStatusBar", { style })
        .catch((error) => console.error("Failed to change status bar:", error));
    });

    // Set up message listeners for one-way messages from native
    rpc.addMessageListener("pushReceived", (event) => {
      console.log("Push received:", event);
      if (event.data?.event?.link) {
        //router.push(event.data.link);
      } else {
        //      const message = event?.message || "New notification";
        //      toast.showToast(message, {
        //        type: "info",
        //        autoHide: 5000,
        //        icon: "notifications",
        //      });
      }
    });

    rpc.addMessageListener("pushOpened", (data) => {
      console.log("Push opened with data:", data);
      // Navigate to route if provided in notification data
      if (data?.link) {
        router.push(data.link);
      }
    });

    rpc.addMessageListener("scanResult", (result) => {
      console.log("Scanned:", result);
      if (scanCallback.value) {
        scanCallback.value(result);
      }
    });

    const openScanner = async (config: ScannerConfig) => {
      console.log("openScanner");
      try {
        await rpc.request("openScanner", config);
      } catch (error) {
        console.error("Failed to open scanner:", error);
        console.warn("Scanner only available in React Native WebView");
      }
    };

    const closeScanner = async () => {
      try {
        await rpc.request("closeScanner");
      } catch (error) {
        console.error("Failed to close scanner:", error);
      }
    };

    const logout = async () => {
      badge.cleared();
      try {
        await rpc.request("logout");
        console.log("Sent logout message to native");
      } catch (error) {
        console.error("Failed to logout:", error);
      }
    };

    // Push registration is best-effort: native rejects when Pushwoosh isn't
    // configured or notification permission is denied, and that must never
    // block or break login.
    const identify = async (identity: UserIdentity) => {
      try {
        await rpc.request("identify", identity);
        console.log("Sent identify message to native");
      } catch (error) {
        console.warn("Failed to identify to native:", error);
      }
    };

    const badge = createBadgeSetter(
      (pendingRequestIds) =>
        rpc.request("setBadge", {
          count: pendingRequestIds.length,
          pendingRequestIds,
        }),
      () => ((window as any).lemmonEnv?.app?.capabilities?.setBadge ?? 0) >= 1,
    );

    // Removes all of the app's notifications (not used yet). Best-effort, and
    // a no-op on builds without the handler, which would leave it hanging.
    const clearNotificationCenter = async () => {
      if (
        ((window as any).lemmonEnv?.app?.capabilities
          ?.clearNotificationCenter ?? 0) < 1
      )
        return;
      try {
        await rpc.request("clearNotificationCenter");
      } catch (error) {
        console.warn("Failed to clear notification center:", error);
      }
    };

    const switchServer = async (id: "primary" | "secondary") => {
      try {
        await rpc.request("switchServer", { id });
        console.log(`Switched to ${id} server`);
      } catch (error) {
        console.error("Failed to switch server:", error);
      }
    };

    const getRoutingConfig = async (): Promise<RoutingConfig> => {
      try {
        const config = await rpc.request("getRoutingConfig");
        console.log("Retrieved routing config:", config);
        return config;
      } catch (error) {
        console.error("Failed to get routing config:", error);
        throw error;
      }
    };

    // Provide native bridge context
    const nativeBridgeContext: NativeBridgeContextType = {
      rpc,
      onScan: (callback: (result: ScanResult) => void) => {
        scanCallback.value = callback;
      },
      openScanner,
      closeScanner,
      logout,
      identify,
      setBadge: badge.set,
      clearNotificationCenter,
      switchServer,
      getRoutingConfig,
    };

    provide(NativeBridgeContextKey, nativeBridgeContext);

    onUnmounted(() => {
      // Clean up RPC transport
      if (rpcTransport.dispose) {
        rpcTransport.dispose();
      }
    });

    return () => <>{slots.default?.()}</>;
  },
});

/**
 * Composable to access native bridge functionality (scanner, RPC, etc.)
 * @example
 * const bridge = useNativeBridge();
 * bridge.onScan((result) => {
 *   console.log("Scanned:", result.data);
 * });
 * bridge.openScanner({
 *   dimensions: { width: 300, height: 300 },
 *   position: { x: 50, y: 100 }
 * });
 * // Switch servers
 * await bridge.switchServer("secondary");
 * // Get routing config
 * const config = await bridge.getRoutingConfig();
 * // Access RPC directly
 * await bridge.rpc.request("openUrl", { url: "https://example.com" });
 */
export function useNativeBridge(): NativeBridgeContextType | null {
  const context = inject<NativeBridgeContextType>(NativeBridgeContextKey);
  if (!globalThis.ReactNativeWebView || !context) {
    return null;
  }
  return context as NativeBridgeContextType;
}

// Legacy export for backward compatibility
/** @deprecated Use useNativeBridge instead */
export const useScanner = useNativeBridge;
